import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Save, DollarSign, CheckCircle2, Circle, Plus,
  Building, CreditCard, Shield, ClipboardList, CalendarClock, Banknote
} from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELD_GROUPS = [
  {
    title: 'Lender & Approval',
    icon: Building,
    color: 'text-purple-400',
    fields: [
      { key: 'lender_name', label: 'Lender', type: 'text' },
      { key: 'approved_amount', label: 'Approved Amount ($)', type: 'number' },
      { key: 'approved_rate', label: 'Approved Rate (%)', type: 'number' },
      { key: 'approved_term', label: 'Term (months)', type: 'number' },
      { key: 'balloon_amount', label: 'Balloon Amount ($)', type: 'number' },
    ],
  },
  {
    title: 'Fees',
    icon: CreditCard,
    color: 'text-amber-400',
    fields: [
      { key: 'establishment_fee', label: 'Establishment Fee ($)', type: 'number' },
      { key: 'account_keeping_fee', label: 'Account Keeping Fee ($)', type: 'number' },
      { key: 'other_fees', label: 'Other Fees', type: 'text' },
    ],
  },
  {
    title: 'Settlement',
    icon: Calendar,
    color: 'text-teal-400',
    fields: [
      { key: 'settlement_date', label: 'Settlement Date', type: 'date' },
      { key: 'payout_amount', label: 'Payout Amount ($)', type: 'number' },
      { key: 'payout_bsb', label: 'Payout BSB', type: 'text' },
      { key: 'payout_account', label: 'Payout Account', type: 'text' },
      { key: 'payout_reference', label: 'Payout Reference', type: 'text' },
    ],
  },
  {
    title: 'Supplier Payment',
    icon: Banknote,
    color: 'text-blue-400',
    fields: [
      { key: 'supplier_name', label: 'Supplier Name', type: 'text' },
      { key: 'supplier_amount', label: 'Amount ($)', type: 'number' },
      { key: 'supplier_bsb', label: 'BSB', type: 'text' },
      { key: 'supplier_account', label: 'Account', type: 'text' },
      { key: 'supplier_invoice_number', label: 'Invoice #', type: 'text' },
    ],
  },
  {
    title: 'Insurance',
    icon: Shield,
    color: 'text-green-400',
    fields: [
      { key: 'insurance_provider', label: 'Provider', type: 'text' },
      { key: 'insurance_policy_number', label: 'Policy Number', type: 'text' },
      { key: 'insurance_status', label: 'Status', type: 'select', options: ['pending', 'confirmed', 'waived'] },
      { key: 'insurance_expiry', label: 'Expiry Date', type: 'date' },
    ],
  },
  {
    title: 'Commission',
    icon: DollarSign,
    color: 'text-emerald-400',
    fields: [
      { key: 'commission_rate', label: 'Rate (%)', type: 'number' },
      { key: 'commission_amount', label: 'Amount ($)', type: 'number' },
      { key: 'commission_gst', label: 'GST ($)', type: 'number' },
      { key: 'commission_paid', label: 'Paid?', type: 'select', options: ['false', 'true'] },
      { key: 'commission_paid_date', label: 'Paid Date', type: 'date' },
    ],
  },
];

export default function SettlementTab({ deal, dealId, onRefresh }) {
  const [settlement, setSettlement] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [creatingTasks, setCreatingTasks] = useState(false);

  const fetchSettlement = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/deals/${dealId}/settlement`);
      if (res.data) {
        setSettlement(res.data);
        setForm(res.data);
      }
    } catch (e) { /* no settlement yet */ }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { fetchSettlement(); }, [fetchSettlement]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/crm/deals/${dealId}/settlement`, {
        deal_id: dealId,
        lender_name: deal.lender_submitted || '',
        approved_amount: deal.amount || null,
        approved_term: deal.term_months || null,
      });
      setSettlement(res.data);
      setForm(res.data);
      toast.success('Settlement record created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELD_GROUPS.forEach(g => g.fields.forEach(f => {
        const val = form[f.key];
        if (val !== undefined && val !== null && val !== '') {
          if (f.type === 'number') payload[f.key] = Number(val);
          else if (f.key === 'commission_paid') payload[f.key] = val === 'true' || val === true;
          else payload[f.key] = val;
        }
      }));
      await axios.put(`${API}/crm/deals/${dealId}/settlement`, payload);
      toast.success('Settlement saved');
      fetchSettlement();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleChecklistToggle = async (itemId, currentState) => {
    try {
      await axios.put(`${API}/crm/deals/${dealId}/settlement/checklist/${itemId}`, {
        is_completed: !currentState,
      });
      fetchSettlement();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    try {
      await axios.post(`${API}/crm/deals/${dealId}/settlement/checklist`, {
        title: newChecklistItem,
      });
      setNewChecklistItem('');
      fetchSettlement();
      toast.success('Item added');
    } catch (e) { toast.error('Failed to add'); }
  };

  const handleCreatePostSettlementTasks = async () => {
    setCreatingTasks(true);
    try {
      const res = await axios.post(`${API}/crm/deals/${dealId}/settlement/post-settlement-tasks`);
      toast.success(`${res.data.tasks?.length || 0} follow-up tasks created`);
      onRefresh();
      fetchSettlement();
    } catch (e) { toast.error('Failed to create tasks'); }
    finally { setCreatingTasks(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div></div>;
  }

  // No settlement yet — show create button
  if (!settlement) {
    return (
      <div className="max-w-3xl" data-testid="settlement-tab">
        <div className="text-center py-16 space-y-4">
          <Calendar className="h-12 w-12 text-teal-400 mx-auto opacity-50" />
          <h2 className="text-white font-medium text-lg">No Settlement Record</h2>
          <p className="text-gray-400 text-sm">Create a settlement record to begin tracking settlement details, checklist items, and post-settlement follow-ups.</p>
          <Button onClick={handleCreate} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="create-settlement-btn">
            {saving ? 'Creating...' : 'Create Settlement Record'}
          </Button>
        </div>
      </div>
    );
  }

  const checklist = settlement.checklist || [];
  const completedCount = checklist.filter(c => c.is_completed).length;
  const checklistPct = checklist.length > 0 ? Math.round(completedCount / checklist.length * 100) : 0;

  return (
    <div className="max-w-4xl space-y-6" data-testid="settlement-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2">
          <Calendar className="h-5 w-5 text-teal-400" /> Settlement
        </h2>
        <div className="flex gap-2">
          <Button onClick={handleCreatePostSettlementTasks} disabled={creatingTasks} size="sm"
            variant="outline" className="border-white/10 text-gray-300 hover:text-white hover:border-teal-500/50" data-testid="create-post-tasks-btn">
            <CalendarClock className="h-4 w-4 mr-1" />
            {creatingTasks ? 'Creating...' : 'Create Follow-ups'}
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-settlement-btn">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Settlement Checklist */}
      <div className="bg-navy-800/50 rounded-xl p-5 border border-white/5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-teal-400" /> Settlement Checklist
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{completedCount}/{checklist.length}</span>
            <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${checklistPct}%` }} />
            </div>
            <span className="text-xs text-teal-400 font-medium">{checklistPct}%</span>
          </div>
        </div>
        <div className="space-y-1.5">
          {checklist.sort((a, b) => a.order - b.order).map(item => (
            <button
              key={item.id}
              onClick={() => handleChecklistToggle(item.id, item.is_completed)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 transition text-left group"
              data-testid={`checklist-item-${item.id}`}
            >
              {item.is_completed ? (
                <CheckCircle2 className="h-5 w-5 text-teal-400 shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-gray-600 group-hover:text-gray-400 shrink-0" />
              )}
              <span className={`text-sm flex-1 ${item.is_completed ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                {item.title}
              </span>
              {item.is_completed && item.completed_at && (
                <span className="text-[10px] text-gray-600">{new Date(item.completed_at).toLocaleDateString()}</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input
            value={newChecklistItem}
            onChange={e => setNewChecklistItem(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddChecklistItem()}
            placeholder="Add checklist item..."
            className="flex-1 bg-navy-800 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-teal-500 focus:outline-none"
            data-testid="add-checklist-input"
          />
          <Button onClick={handleAddChecklistItem} size="sm" variant="outline" className="border-white/10 text-gray-300" data-testid="add-checklist-btn">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Field Groups */}
      {FIELD_GROUPS.map(group => {
        const GroupIcon = group.icon;
        return (
          <div key={group.title} className="bg-navy-800/50 rounded-xl p-5 border border-white/5">
            <h3 className={`font-medium mb-4 flex items-center gap-2 text-sm ${group.color}`}>
              <GroupIcon className="h-4 w-4" /> {group.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.fields.map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                  {f.type === 'select' ? (
                    <select
                      value={form[f.key] !== undefined ? String(form[f.key]) : ''}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-teal-500 focus:outline-none"
                      data-testid={`settlement-${f.key}`}
                    >
                      <option value="">Select...</option>
                      {f.options.map(o => (
                        <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type}
                      value={form[f.key] || ''}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-teal-500 focus:outline-none"
                      data-testid={`settlement-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Post-Settlement Status */}
      {settlement.post_settlement_status && (
        <div className="bg-navy-800/50 rounded-xl p-5 border border-teal-500/20">
          <h3 className="text-teal-400 font-medium flex items-center gap-2 text-sm mb-2">
            <CalendarClock className="h-4 w-4" /> Post-Settlement
          </h3>
          <p className="text-gray-300 text-sm">
            Status: <span className="text-white font-medium">{settlement.post_settlement_status.replace(/_/g, ' ')}</span>
          </p>
          {settlement.next_followup_date && (
            <p className="text-gray-400 text-xs mt-1">
              Next follow-up: {new Date(settlement.next_followup_date).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
